import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'cookie-consent';

function getSavedPreferences() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

const DEFAULT_PURPOSES = {
  storage: true,
  basicAds: true,
  createProfile: false,
  useProfile: false,
  createContent: false,
  useContent: false,
  measureAds: true,
  measureContent: true,
  audience: true,
  develop: true,
  selectContent: true,
  security: true,
  deliver: true,
  privacyPrefs: true,
  combineData: false,
  linkDevices: false,
  identifyDevices: true,
  preciseGeo: false,
};

function CookiesManageView({ onBack, onSave, t }) {
  const [expanded, setExpanded] = useState({});
  const [purposes, setPurposes] = useState(() => {
    const saved = getSavedPreferences();
    return saved ? saved.purposes : DEFAULT_PURPOSES;
  });

  const toggle = (key) => setPurposes(p => ({ ...p, [key]: !p[key] }));
  const toggleExpanded = (key) => setExpanded(e => ({ ...e, [key]: !e[key] }));

  const handleSave = () => {
    const data = { consented: true, purposes, updatedAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    onSave();
  };

  const items = [
    { key: 'storage', title: t('landing.cookies.mgStorage'), desc: t('landing.cookies.mgStorageDesc'), consent: 123, li: null },
    { key: 'basicAds', title: t('landing.cookies.mgBasicAds'), desc: t('landing.cookies.mgBasicAdsDesc'), consent: 75, li: null },
    { key: 'createProfile', title: t('landing.cookies.mgCreateProfile'), desc: t('landing.cookies.mgCreateProfileDesc'), consent: 106, li: null },
    { key: 'useProfile', title: t('landing.cookies.mgUseProfile'), desc: t('landing.cookies.mgUseProfileDesc'), consent: 106, li: null },
    { key: 'createContent', title: t('landing.cookies.mgCreateContent'), desc: t('landing.cookies.mgCreateContentDesc'), consent: 29, li: null },
    { key: 'useContent', title: t('landing.cookies.mgUseContent'), desc: t('landing.cookies.mgUseContentDesc'), consent: 27, li: null },
    { key: 'measureAds', title: t('landing.cookies.mgMeasureAds'), desc: t('landing.cookies.mgMeasureAdsDesc'), consent: 74, li: 61 },
    { key: 'measureContent', title: t('landing.cookies.mgMeasureContent'), desc: t('landing.cookies.mgMeasureContentDesc'), consent: 27, li: 21 },
    { key: 'audience', title: t('landing.cookies.mgAudience'), desc: t('landing.cookies.mgAudienceDesc'), consent: 55, li: 33 },
    { key: 'develop', title: t('landing.cookies.mgDevelop'), desc: t('landing.cookies.mgDevelopDesc'), consent: 62, li: 53 },
    { key: 'selectContent', title: t('landing.cookies.mgSelectContent'), desc: t('landing.cookies.mgSelectContentDesc'), consent: 13, li: 5 },
    { key: 'security', title: t('landing.cookies.mgSecurity'), desc: t('landing.cookies.mgSecurityDesc'), consent: null, li: null },
    { key: 'deliver', title: t('landing.cookies.mgDeliver'), desc: t('landing.cookies.mgDeliverDesc'), consent: null, li: null },
    { key: 'privacyPrefs', title: t('landing.cookies.mgPrivacyPrefs'), desc: t('landing.cookies.mgPrivacyPrefsDesc'), consent: null, li: null },
    { key: 'combineData', title: t('landing.cookies.mgCombineData'), desc: t('landing.cookies.mgCombineDataDesc'), consent: null, li: null },
    { key: 'linkDevices', title: t('landing.cookies.mgLinkDevices'), desc: t('landing.cookies.mgLinkDevicesDesc'), consent: null, li: null },
    { key: 'identifyDevices', title: t('landing.cookies.mgIdentifyDevices'), desc: t('landing.cookies.mgIdentifyDevicesDesc'), consent: null, li: null },
    { key: 'preciseGeo', title: t('landing.cookies.mgPreciseGeo'), desc: t('landing.cookies.mgPreciseGeoDesc'), consent: null, li: null },
  ];

  return (
    <div className="max-h-[70vh] overflow-y-auto space-y-2 pr-1">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onBack} className="text-zinc-400 hover:text-white transition-colors shrink-0">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></svg>
        </button>
        <h2 className="text-lg font-bold text-white">{t('landing.cookies.mgTitle')}</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-6">{t('landing.cookies.mgSubtitle')}</p>
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-cyan-400 mb-2">{t('landing.cookies.mgProviders')}</h3>
      </div>
      {items.map(item => (
        <div key={item.key} className="border border-white/10 rounded-lg bg-zinc-900/50">
          <button
            type="button"
            onClick={() => toggleExpanded(item.key)}
            className="w-full flex items-center justify-between p-3 text-left"
          >
            <span className="text-sm text-white font-medium flex-1 pr-4">{item.title}</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="text-zinc-400 shrink-0 transition-transform duration-200" style={{ transform: expanded[item.key] ? 'rotate(180deg)' : 'rotate(0deg)' }}><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          {expanded[item.key] && (
            <div className="px-3 pb-3 space-y-3">
              <p className="text-xs text-zinc-400 leading-relaxed">{item.desc}</p>
              <div className="flex items-center gap-4 flex-wrap">
                {item.consent !== null && (
                  <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                    <input type="checkbox" checked={purposes[item.key]} onChange={() => toggle(item.key)} className="accent-cyan-500 w-3.5 h-3.5" />
                    {t('landing.cookies.mgConsentLabel')} ({item.consent})
                  </label>
                )}
                {item.li !== null && (
                  <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                    <input type="checkbox" checked={purposes[item.key]} onChange={() => toggle(item.key)} className="accent-cyan-500 w-3.5 h-3.5" />
                    {t('landing.cookies.mgLiLabel')} ({item.li})
                  </label>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
      <div className="sticky bottom-0 bg-zinc-950 pt-4 pb-2 flex flex-col sm:flex-row gap-3 mt-4">
        <button
          type="button"
          onClick={handleSave}
          className="flex-1 rounded-lg bg-cyan-500 px-6 py-3 text-sm font-semibold text-white hover:bg-cyan-400 transition-colors"
        >
          {t('landing.cookies.mgSave')}
        </button>
      </div>
    </div>
  );
}

function CookieBanner() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showManage, setShowManage] = useState(false);

  useEffect(() => {
    const saved = getSavedPreferences();
    if (!saved || !saved.consented) {
      setIsVisible(true);
    }
  }, []);

  const handleConsent = () => {
    const data = { consented: true, purposes: DEFAULT_PURPOSES, updatedAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => {}} />
      <div className="relative w-full sm:max-w-lg bg-zinc-950 border border-white/10 shadow-2xl shadow-black/60 text-white
        rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
        {!showManage ? (
          <>
            <div className="flex items-start gap-3 mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#06b6d4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="mt-0.5 shrink-0"><path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/><path d="M8.5 8.5v.01"/><path d="M16 15v.01"/><path d="M12 12v.01"/><path d="M11 17v.01"/><path d="M7 14v.01"/></svg>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-white">{t('landing.cookies.title')}</h2>
                <p className="mt-0.5 text-sm text-zinc-400">{t('landing.cookies.subtitle')}</p>
              </div>
            </div>

            <p className="text-sm leading-relaxed text-zinc-300 mb-3">{t('landing.cookies.purposes')}</p>

            <ul className="space-y-1 mb-3 text-sm text-zinc-400">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
                {t('landing.cookies.purpose1')}
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
                {t('landing.cookies.purpose2')}
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
                {t('landing.cookies.purpose3')}
              </li>
            </ul>

            <p className="text-sm text-zinc-300 mb-3">{t('landing.cookies.storageInfo')}</p>

            <button
              type="button"
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors mb-3 flex items-center gap-1"
            >
              {t('landing.cookies.moreInfo')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className={`transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`}><polyline points="6 9 12 15 18 9"/></svg>
            </button>

            {showDetails && (
              <div className="space-y-2 mb-3 border border-white/10 rounded-lg p-3 bg-zinc-900/50 text-sm text-zinc-300 max-h-48 overflow-y-auto">
                <p><span className="text-cyan-400">¿{t('landing.cookies.q1')}</span> {t('landing.cookies.a1')}</p>
                <p><span className="text-cyan-400">¿{t('landing.cookies.q2')}</span> {t('landing.cookies.a2')}</p>
                <p><span className="text-cyan-400">¿{t('landing.cookies.q3')}</span> {t('landing.cookies.a3')}</p>
                <p><span className="text-cyan-400">¿{t('landing.cookies.q4')}</span> {t('landing.cookies.a4')}</p>
                <p className="mt-2 text-zinc-400">{t('landing.cookies.partnersInfo')} <span className="text-cyan-400">{t('landing.cookies.partnerList')}</span></p>
                <p className="text-zinc-400">{t('landing.cookies.geoInfo')}</p>
                <p className="text-zinc-400">{t('landing.cookies.legitimateInterest')}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleConsent}
                className="flex-1 rounded-lg bg-cyan-500 px-6 py-3 text-sm font-semibold text-white hover:bg-cyan-400 transition-colors"
              >
                {t('landing.cookies.consent')}
              </button>
              <button
                type="button"
                onClick={() => setShowManage(true)}
                className="flex-1 rounded-lg border border-white/20 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                {t('landing.cookies.manage')}
              </button>
            </div>
          </>
        ) : (
          <CookiesManageView
            onBack={() => setShowManage(false)}
            onSave={() => setIsVisible(false)}
            t={t}
          />
        )}
      </div>
    </div>
  );
}

export default CookieBanner;
